import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Plus, Trash2, UserPlus, Users, Mail } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ListsPage() {
  const [lists, setLists] = useState([]);
  const [businesses, setBusinesses] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: '', business_ids: [], description: '' });
  const [selectedList, setSelectedList] = useState(null);
  const [listContacts, setListContacts] = useState([]);
  const [addContactOpen, setAddContactOpen] = useState(false);
  const [selectedContactIds, setSelectedContactIds] = useState([]);

  const fetchAll = useCallback(async () => {
    try {
      const [lRes, bRes, cRes] = await Promise.all([
        axios.get(`${API}/lists`, { withCredentials: true }),
        axios.get(`${API}/businesses`, { withCredentials: true }),
        axios.get(`${API}/contacts`, { params: { limit: 500 }, withCredentials: true }),
      ]);
      setLists(lRes.data);
      setBusinesses(bRes.data);
      setContacts(cRes.data.contacts || []);
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const fetchListContacts = async (listId) => {
    try {
      const res = await axios.get(`${API}/lists/${listId}/contacts`, { withCredentials: true });
      setListContacts(res.data);
    } catch (e) { console.error(e); }
  };

  const handleCreateList = async () => {
    if (form.business_ids.length === 0) {
      toast.error('Select at least one business');
      return;
    }
    try {
      await axios.post(`${API}/lists`, form, { withCredentials: true });
      toast.success('List created');
      setOpen(false);
      setForm({ name: '', business_ids: [], description: '' });
      fetchAll();
    } catch (e) { toast.error('Failed to create list'); }
  };

  const handleDeleteList = async (id) => {
    if (!window.confirm('Delete this list?')) return;
    try {
      await axios.delete(`${API}/lists/${id}`, { withCredentials: true });
      toast.success('List deleted');
      if (selectedList === id) setSelectedList(null);
      fetchAll();
    } catch (e) { toast.error('Failed to delete'); }
  };

  const handleAddContacts = async () => {
    if (!selectedList || selectedContactIds.length === 0) return;
    try {
      await axios.post(`${API}/lists/${selectedList}/contacts`, { contact_ids: selectedContactIds }, { withCredentials: true });
      toast.success('Contacts added to list');
      setAddContactOpen(false);
      setSelectedContactIds([]);
      fetchListContacts(selectedList);
      fetchAll();
    } catch (e) { toast.error('Failed to add contacts'); }
  };

  const handleRemoveContact = async (contactId) => {
    try {
      await axios.delete(`${API}/lists/${selectedList}/contacts/${contactId}`, { withCredentials: true });
      toast.success('Contact removed');
      fetchListContacts(selectedList);
      fetchAll();
    } catch (e) { toast.error('Failed to remove'); }
  };

  const selectList = (listId) => {
    setSelectedList(listId);
    fetchListContacts(listId);
  };

  const getBizNames = (ids) => {
    if (!ids || ids.length === 0) return '—';
    return ids.map(id => businesses.find(b => b.business_id === id)?.name || id).join(', ');
  };

  const toggleContact = (id) => {
    setSelectedContactIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const toggleFormBusiness = (bid) => {
    setForm(p => ({
      ...p,
      business_ids: p.business_ids.includes(bid)
        ? p.business_ids.filter(x => x !== bid)
        : [...p.business_ids, bid]
    }));
  };

  return (
    <div className="flex-1 p-8" data-testid="lists-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-white" style={{ fontFamily: 'Manrope' }}>Mailing Lists</h1>
          <p className="text-[#B0B0B0] text-sm mt-1">Manage subscriber lists for your campaigns</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="create-list-btn" className="bg-[#00FF99] text-[#0A0A0A] font-bold hover:bg-[#00E68A] rounded-md">
              <Plus size={16} className="mr-2" /> New List
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-[#121212] border-[rgba(255,255,255,0.12)] text-white max-w-md">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Manrope' }}>New Mailing List</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div>
                <Label className="text-xs text-[#B0B0B0] uppercase tracking-widest">List Name</Label>
                <Input data-testid="list-name-input" value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
                  className="bg-transparent border-[rgba(255,255,255,0.12)] text-white mt-1" placeholder="Newsletter subscribers" />
              </div>
              <div>
                <Label className="text-xs text-[#B0B0B0] uppercase tracking-widest mb-2 block">Businesses (select one or more)</Label>
                <div className="space-y-1 max-h-40 overflow-y-auto border border-[rgba(255,255,255,0.12)] rounded-md p-2">
                  {businesses.map(b => (
                    <label key={b.business_id} className="flex items-center gap-3 p-1.5 rounded hover:bg-[#1A1A1A] cursor-pointer">
                      <input
                        type="checkbox"
                        checked={form.business_ids.includes(b.business_id)}
                        onChange={() => toggleFormBusiness(b.business_id)}
                        className="accent-[#00FF99]"
                        data-testid={`list-business-check-${b.business_id}`}
                      />
                      <span className="text-sm text-white">{b.name}</span>
                    </label>
                  ))}
                  {businesses.length === 0 && (
                    <p className="text-xs text-[#808080] p-2">No businesses found</p>
                  )}
                </div>
                {form.business_ids.length > 0 && (
                  <p className="text-xs text-[#00FF99] mt-1">{form.business_ids.length} business{form.business_ids.length !== 1 ? 'es' : ''} selected</p>
                )}
              </div>
              <div>
                <Label className="text-xs text-[#B0B0B0] uppercase tracking-widest">Description</Label>
                <Input value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))}
                  className="bg-transparent border-[rgba(255,255,255,0.12)] text-white mt-1" placeholder="Optional description" />
              </div>
              <Button data-testid="create-list-submit" onClick={handleCreateList} className="w-full bg-[#00FF99] text-[#0A0A0A] font-bold hover:bg-[#00E68A]">
                Create List
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Lists panel */}
        <div className="space-y-3">
          {lists.map(l => (
            <button key={l.list_id} onClick={() => selectList(l.list_id)}
              className={`w-full text-left p-4 rounded-lg border transition-colors duration-200 ${
                selectedList === l.list_id
                  ? 'bg-[#121212] border-[#00FF99]'
                  : 'bg-[#121212] border-[rgba(255,255,255,0.12)] hover:border-[rgba(255,255,255,0.25)]'
              }`}
            >
              <div className="flex items-center justify-between">
                <div className="flex-1 min-w-0 pr-2">
                  <h3 className="text-white font-medium text-sm">{l.name}</h3>
                  <p className="text-xs text-[#808080] mt-0.5 truncate">{getBizNames(l.business_ids)}</p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <span className="text-xs text-[#B0B0B0] bg-[#1A1A1A] px-2 py-0.5 rounded">
                    {l.contact_count || 0} contacts
                  </span>
                  <button onClick={(e) => { e.stopPropagation(); handleDeleteList(l.list_id); }}
                    className="p-1 text-[#808080] hover:text-[#FF3B30]">
                    <Trash2 size={12} />
                  </button>
                </div>
              </div>
            </button>
          ))}
          {lists.length === 0 && (
            <div className="text-center py-8 text-[#808080]">
              <Mail size={24} className="mx-auto mb-2 opacity-50" />
              <p className="text-sm">No lists yet</p>
            </div>
          )}
        </div>

        {/* List detail panel */}
        <div className="lg:col-span-2">
          {selectedList ? (
            <div className="bg-[#121212] border border-[rgba(255,255,255,0.12)] rounded-lg p-5">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="text-lg font-bold text-white" style={{ fontFamily: 'Manrope' }}>
                    {lists.find(l => l.list_id === selectedList)?.name}
                  </h2>
                  <p className="text-xs text-[#808080] mt-0.5">
                    {getBizNames(lists.find(l => l.list_id === selectedList)?.business_ids)}
                  </p>
                </div>
                <Dialog open={addContactOpen} onOpenChange={setAddContactOpen}>
                  <DialogTrigger asChild>
                    <Button size="sm" data-testid="add-contacts-to-list-btn" className="bg-[#00FF99] text-[#0A0A0A] font-bold hover:bg-[#00E68A] text-xs">
                      <UserPlus size={14} className="mr-1" /> Add Contacts
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="bg-[#121212] border-[rgba(255,255,255,0.12)] text-white max-w-md">
                    <DialogHeader>
                      <DialogTitle style={{ fontFamily: 'Manrope' }}>Add Contacts to List</DialogTitle>
                    </DialogHeader>
                    <div className="mt-2 max-h-60 overflow-y-auto space-y-1">
                      {contacts.map(c => (
                        <label key={c.contact_id} className="flex items-center gap-3 p-2 rounded hover:bg-[#1A1A1A] cursor-pointer">
                          <input type="checkbox" checked={selectedContactIds.includes(c.contact_id)}
                            onChange={() => toggleContact(c.contact_id)}
                            className="accent-[#00FF99]" />
                          <div>
                            <p className="text-sm text-white">{c.name}</p>
                            <p className="text-xs text-[#808080]">{c.email}</p>
                          </div>
                        </label>
                      ))}
                    </div>
                    <Button onClick={handleAddContacts} className="w-full mt-3 bg-[#00FF99] text-[#0A0A0A] font-bold hover:bg-[#00E68A]">
                      Add {selectedContactIds.length} Contact{selectedContactIds.length !== 1 ? 's' : ''}
                    </Button>
                  </DialogContent>
                </Dialog>
              </div>

              <Table>
                <TableHeader>
                  <TableRow className="border-b border-[rgba(255,255,255,0.12)] hover:bg-transparent">
                    <TableHead className="text-[#B0B0B0] text-xs uppercase tracking-widest">Name</TableHead>
                    <TableHead className="text-[#B0B0B0] text-xs uppercase tracking-widest">Email</TableHead>
                    <TableHead className="text-[#B0B0B0] text-xs uppercase tracking-widest w-16">Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {listContacts.map(c => (
                    <TableRow key={c.contact_id} className="border-b border-[rgba(255,255,255,0.06)] hover:bg-[#1A1A1A]">
                      <TableCell className="text-white text-sm">{c.name}</TableCell>
                      <TableCell className="text-[#B0B0B0] text-sm">{c.email}</TableCell>
                      <TableCell>
                        <button onClick={() => handleRemoveContact(c.contact_id)}
                          className="p-1 text-[#808080] hover:text-[#FF3B30]">
                          <Trash2 size={12} />
                        </button>
                      </TableCell>
                    </TableRow>
                  ))}
                  {listContacts.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={3} className="text-center text-[#808080] py-8">
                        No contacts in this list yet
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="bg-[#121212] border border-[rgba(255,255,255,0.12)] rounded-lg p-12 text-center text-[#808080]">
              <Users size={32} className="mx-auto mb-3 opacity-50" />
              <p>Select a list to view its contacts</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
